import { useEffect, useRef, useState } from 'react'
import {
  EMPTY_QUEUE,
  useMessageQueue,
  type QueuedMessage,
} from '@/stores/messageQueue'
import type { ConversationRunStatus } from '@/types'

interface UseQueueDrainOptions {
  conversationId: string | undefined
  enabled: boolean
  isRunActive: boolean
  /** The local stream's last run ended in an error the retries could not clear. */
  runFailed: boolean
  runStatus: ConversationRunStatus | null | undefined
  send: (item: QueuedMessage) => Promise<'aborted' | 'failed' | undefined>
}

// Drains only while its conversation is open; a server-side drain after RUN_FINISHED is the upgrade.
export function useQueueDrain({
  conversationId,
  enabled,
  isRunActive,
  runFailed,
  runStatus,
  send,
}: UseQueueDrainOptions) {
  const queue =
    useMessageQueue((s) =>
      conversationId ? s.queues[conversationId] : undefined
    ) ?? EMPTY_QUEUE
  // State, not a ref: the send resolves after the run already flipped
  // inactive, so clearing it must re-run the effect or the next item stalls.
  const [draining, setDraining] = useState(false)
  const prevStatus = useRef<ConversationRunStatus | null | undefined>(undefined)
  const prevRunFailed = useRef(false)
  const prevConversationId = useRef<string | undefined>(undefined)
  // The drained item until its run settles. A failed attempt retries in the
  // background after the send resolves, so resolving says nothing yet.
  const inFlight = useRef<QueuedMessage | null>(null)

  useEffect(() => {
    if (!conversationId) return
    const { hold, dequeueHead, requeue } = useMessageQueue.getState()
    const putBack = () => {
      const item = inFlight.current
      inFlight.current = null
      if (item) requeue(item.conversationId, item)
    }
    if (prevConversationId.current !== conversationId) {
      prevConversationId.current = conversationId
      prevStatus.current = undefined
      inFlight.current = null
    }
    // Edge-triggered: a failure from before this turn must not hold the queue
    // when the turn after it ends well, nor hold it again after a Resume.
    const becameFailed =
      (runStatus === 'failed' && prevStatus.current !== 'failed') ||
      (runFailed && !prevRunFailed.current)
    prevStatus.current = runStatus
    prevRunFailed.current = runFailed
    if (becameFailed) {
      putBack()
      hold(conversationId)
      return
    }
    if (!enabled || isRunActive || draining) return
    inFlight.current = null
    const item = dequeueHead(conversationId)
    if (!item) return
    inFlight.current = item
    setDraining(true)
    void send(item).then((result) => {
      setDraining(false)
      if (result === 'aborted') inFlight.current = null
      if (result === 'failed') {
        putBack()
        hold(item.conversationId)
      }
    })
  }, [
    conversationId,
    enabled,
    isRunActive,
    runFailed,
    runStatus,
    queue,
    send,
    draining,
  ])
}
